<template>
  <div class="order-view">
    <van-sticky z-index="99">
      <div class="header">
        <div class="header-left">
          <h1 class="page-title">My Orders</h1>
          <p class="page-subtitle">Manage your active & past tasks</p>
        </div>
        <div class="user-brief">
          <van-image round width="40px" height="40px" :src="imageUrl(user.avatar)"/>
        </div>
      </div>

      <van-tabs
          v-model:active="active"
          line-width="32px"
          line-height="4px"
          class="custom-tabs"
          :border="false"
      >
        <van-tab title="Ongoing" name="0"/>
        <van-tab title="Completed" name="1"/>
        <van-tab title="Frozen" name="2"/>
        <van-tab title="Pending" name="3"/>
      </van-tabs>
    </van-sticky>

    <div class="content-area">
      <div v-if="active === '1'" class="search-box">
        <van-search
            v-model="searchValue"
            placeholder="Search by Order No"
            shape="round"
            @search="onSearch"
            @clear="onSearch"
        />
      </div>

      <van-list
          v-model:loading="loading"
          :finished="finished"
          loading-text="Loading..."
          finished-text="No more orders"
          @load="onLoadList"
          class="order-list"
      >
        <div v-for="(item, index) in ordersList" :key="index" class="order-card">
          <div class="card-header">
            <div class="amount-section">
              <span class="amount">{{ item.payAmount }}</span>
              <span class="currency">{{ item.currency }}</span>
            </div>
            <div class="header-right">
              <van-button size="small" plain round class="btn-cancel" @click="handleCancel(item)">Give Up</van-button>
              <div class="status-badge" :class="getStatusClass(item.payState)">
                <van-icon :name="getStatusIcon(item.payState)"/>
                <span>{{ pay_order_state_map[item.payState] || 'Processing' }}</span>
              </div>
              <div v-if="active === '0'" class="copy-all-btn" @click="copyAll(item)">
                <van-icon name="copy"/>
                <span>Copy All</span>
              </div>
            </div>
          </div>

          <div class="card-body">
            <div class="info-row">
              <span class="label">Payment Method</span>
              <span class="value">{{ getPayTypeLabel(item.withdrawOrder?.payType || item.payType) }}</span>
            </div>

            <div v-if="shouldShowPayoutBankName(item)" class="info-row">
              <span class="label">Bank</span>
              <span class="value">{{ item.withdrawOrder.bankName }}</span>
            </div>

            <div class="info-row">
              <span class="label">Recipient</span>
              <div class="value-with-copy copyable-field" @click.stop="copyText(getDisplayName(item, true))">
                <span>{{ getDisplayName(item, true) }}</span>
                <div class="copy-badge">
                  <van-icon name="copy-o"/>
                  <span class="copy-hint">Tap to copy</span>
                </div>
              </div>
            </div>

            <div v-if="shouldShowPayoutAccountRow(item)" class="info-row">
              <span class="label">{{ getPayoutAccountLabel(item) }}</span>
              <div
                  v-if="shouldHideAccountNo(item)"
                  class="value-with-copy account-hidden"
              >
                <span class="text-ellipsis">{{ getDisplayAccountNo(item) }}</span>
              </div>
              <div
                  v-else
                  class="value-with-copy copyable-field"
                  @click.stop="copyAccountNo(item)"
              >
                <span class="text-ellipsis">{{ getDisplayAccountNo(item) }}</span>
                <div class="copy-badge">
                  <van-icon name="copy-o"/>
                  <span class="copy-hint">Tap to copy</span>
                </div>
              </div>
            </div>

            <div v-if="(item.withdrawOrder?.receiverAccountRemark || item.accountRemark)" class="info-row">
              <span class="label">Account Remark</span>
              <span class="value">{{ item.withdrawOrder?.receiverAccountRemark || item.accountRemark }}</span>
            </div>

            <div v-if="shouldShowPayoutPhone(item)"
                 class="info-row">
              <span class="label">Phone</span>
              <span class="value">{{ item.withdrawOrder.phone }}</span>
            </div>

            <div v-if="item.withdrawOrder?.exts" class="info-row">
              <span class="label">{{ getWithdrawExtsLabel(item) }}</span>
              <div class="value-with-copy copyable-field" @click.stop="copyText(getDisplayIban(item))">
                <span class="text-ellipsis">{{ getDisplayIban(item) }}</span>
                <div class="copy-badge" v-if="item.type==='bank'">
                  <van-icon name="copy-o"/>
                  <span class="copy-hint">Tap to copy</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card-footer">
            <div class="timer-section" v-if="active === '0' && item.expireTime">
              <van-icon name="clock-o"/>
              <van-count-down
                  v-if="getRemainingTime(item.expireTime) > 0 && !item.hiddenEp"
                  :time="getRemainingTime(item.expireTime)"
                  format="mm:ss"
                  @finish="countDownFinish(item)"
              />
              <span v-else class="expired">Expired</span>
            </div>
            <div class="footer-row">
              <div class="order-no copyable-field" @click.stop="copyText(item.orderNo)">
                <span class="order-no-value">#{{ formatOrderNoShort(item.orderNo) }}</span>
                <span class="order-time" v-if="item.createTime">{{ formatDate(item.createTime) }}</span>
                <div class="copy-badge">
                  <van-icon name="copy-o"/>
                  <span class="copy-hint">Tap to copy</span>
                </div>
              </div>
              <van-button v-if="active === '0'" size="small" type="primary" round class="btn-confirm"
                          @click="completeNow(item)">Submit
              </van-button>
            </div>
          </div>

          <div class="actions" v-if="active === '3' && item.remark === 'uev'">
            <van-button size="small" type="primary" round class="btn-confirm" @click="completeNow(item)">Reupload
            </van-button>
          </div>
        </div>
      </van-list>
    </div>

    <!-- Upload Popup -->
    <van-popup
        v-model:show="completeConfirm"
        round
        position="center"
        class="upload-popup"
        :style="{ width: '90%', maxWidth: '500px' }"
    >
      <div class="popup-header">
        <h3>Submit Evidence</h3>
        <van-icon name="cross" @click="completeConfirm = false"/>
      </div>
      <div class="popup-body">
        <!-- Recipient Information Section -->
        <div class="recipient-info-section">
          <div class="section-title">
            <van-icon name="user-circle-o"/>
            <span>Recipient Information</span>
          </div>
          <div class="info-card">
            <div class="info-item-row">
              <span class="info-label">Amount</span>
              <span class="info-value highlight">{{ dataFrom.payAmount }} {{ dataFrom.currency }}</span>
            </div>
            <div class="info-item-row" v-if="dataFrom.incomeRatio !== undefined && dataFrom.incomeRatio !== null">
              <span class="info-label">Profit Rate</span>
              <span class="info-value highlight">{{ formatPercentage(dataFrom.incomeRatio) }}</span>
            </div>
            <div class="info-item-row">
              <span class="info-label">Name</span>
              <span class="info-value">{{ getDisplayName(dataFrom, true) || 'N/A' }}</span>
            </div>
            <div class="info-item-row account-row">
              <span class="info-label">Account</span>
              <span
                  v-if="shouldHideAccountNo(dataFrom)"
                  class="info-value account-hidden"
              >{{ getDisplayAccountNo(dataFrom) || 'N/A' }}</span>
              <span
                  v-else
                  class="info-value account-value"
                  @click.stop="copyAccountNo(dataFrom)"
              >
                {{ getDisplayAccountNo(dataFrom) || 'N/A' }}
                <van-icon name="copy-o" class="copy-icon-small"/>
              </span>
            </div>
            <div v-if="dataFrom.withdrawOrder?.receiverAccountRemark || dataFrom.accountRemark" class="info-item-row">
              <span class="info-label">Account Remark</span>
              <span class="info-value">{{ dataFrom.withdrawOrder?.receiverAccountRemark || dataFrom.accountRemark }}</span>
            </div>
          </div>
        </div>

        <p class="upload-tip">Please upload the transfer receipt (screenshot of SMS or bank record)</p>

        <van-uploader
            v-model="fileList"
            max-count="3"
            class="custom-uploader"
        >
          <div class="uploader-placeholder">
            <van-icon name="photograph" size="32"/>
            <span>Upload Receipt</span>
          </div>
        </van-uploader>

        <div class="popup-actions">
          <van-button v-if="dataFrom.remark !== 'uev'" plain round class="btn-invalid" @click="handleInvalid">
            Invalid
          </van-button>
          <van-button type="primary" round class="btn-submit" @click="handleSubmit">
            Confirm
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import {onMounted, ref, watch} from 'vue'
import {showConfirmDialog, showToast} from 'vant'
import {imageUrl} from '@/utils'
import {getWithdrawExtsLabel} from '@/utils/withdrawExts'
import {
  buildPayoutCopyText,
  getPayTypeLabel,
  getPayoutAccountLabel,
  shouldShowPayoutAccountRow,
  shouldShowPayoutBankName,
  shouldShowPayoutPhone,
} from '@/utils/payoutDisplay'
import {
  formatDate,
  getDisplayName,
  getUserInfo,
  listPay_order,
  pay_order_state_map,
  updatePay_orderCancel,
  updatePay_orderConfirmV2,
  updatePay_orderInvalid,
} from '@/utils/api'
import {
  ensureReceiverAccountRemarkHideKeywords,
  getDisplayAccountNo,
  shouldHideAccountNo,
} from '@/utils/receiverAccountRemarkHide'

const active = ref('0')
const searchValue = ref('')
const loading = ref(false)
const finished = ref(false)
const ordersList = ref([])
const user = ref({})
const completeConfirm = ref(false)
const fileList = ref([])
const dataFrom = ref({})

const queryParams = ref({
  pageNum: 0,
  pageSize: 20,
  orderByColumn: 'id',
  isAsc: 'ASC',
  payState: 'p_w',
})

const getStatusClass = (state) => {
  const map = {
    'p_w': 'status-ongoing',
    'p_s': 'status-success',
    'p_f': 'status-failed',
  }
  return map[state] || 'status-default'
}

const getStatusIcon = (state) => {
  const map = {
    'p_w': 'clock-o',
    'p_s': 'checked',
    'p_f': 'close',
  }
  return map[state] || 'info-o'
}

const getRemainingTime = (expireTime) => {
  const now = Date.now() + (480 + new Date().getTimezoneOffset()) * 60000
  const expire = new Date(expireTime).getTime()
  return Math.max(0, expire - now)
}

watch(active, (newVal) => {
  queryParams.value.payState = null
  queryParams.value.remark = null
  switch (newVal) {
    case '0':
      queryParams.value.payState = 'p_w';
      break
    case '1':
      queryParams.value.payState = '!p_w';
      break
    case '2':
      queryParams.value.remark = 'djz,djwc'
      break
    case '3':
      queryParams.value.payState = 'p_d'
      break
  }
  queryParams.value.pageNum = 0
  queryParams.value.isAsc = newVal === '0' ? 'ASC' : 'DESC'
  ordersList.value = []
  finished.value = false
  onLoadList()
})

const onLoadList = async () => {
  try {
    await ensureReceiverAccountRemarkHideKeywords()
    loading.value = true
    queryParams.value.pageNum++
    const res = await listPay_order(queryParams.value)
    if (res.rows && res.rows.length > 0) {
      // 检测 MyBatis reasonable=true 的情况：如果返回的数据第一条已存在于列表中，说明已经到底了
      const firstNewItemId = res.rows[0]?.id
      const isDuplicate = ordersList.value.length > 0 && ordersList.value.some(item => item.id === firstNewItemId)

      if (isDuplicate) {
        // 返回的数据与已有数据重复，说明已经到底了（reasonable=true导致返回了第一页）
        finished.value = true
      } else {
        // 正常情况：添加新数据
        ordersList.value = [...ordersList.value, ...res.rows]
        // 如果返回的数据少于pageSize，说明已经到底了
        if (res.rows.length < queryParams.value.pageSize) {
          finished.value = true
        }
      }
    } else {
      finished.value = true
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  queryParams.value.pageNum = 0
  queryParams.value.orderNo = searchValue.value
  ordersList.value = []
  onLoadList()
}

const copyAccountNo = (item) => {
  if (shouldHideAccountNo(item)) {
    showToast('Account hidden')
    return
  }
  copyText(item.withdrawOrder?.receiverAccount)
}

const copyText = (text) => {
  if (!text) {
    showToast('No content to copy')
    return
  }
  const textToCopy = String(text)
  navigator.clipboard.writeText(textToCopy).then(() => {
    showToast('Copied')
  }).catch(() => {
    // 降级方案：使用传统方法
    const textArea = document.createElement('textarea')
    textArea.value = textToCopy
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      showToast('Copied')
    } catch (err) {
      showToast('Copy failed')
    }
    document.body.removeChild(textArea)
  })
}

const copyAll = (item) => {
  const text = buildPayoutCopyText(item, {
    getDisplayName,
    shouldHideAccountNo,
    getDisplayAccountNo,
  })
  navigator.clipboard.writeText(text).then(() => {
    showToast('All info copied')
  })
}

/** 订单号缩略显示：仅显示后 10 位，点击复制仍为完整订单号 */
function formatOrderNoShort(orderNo) {
  if (!orderNo) return ''
  const s = String(orderNo)
  if (s.length <= 10) return s
  return s.slice(-10)
}

/** 扩展字段 IBAN：bank 显示完整，其他类型掩码后 5 位 */
function getDisplayIban(item) {
  const iban = item.withdrawOrder?.exts
  if (!iban) return ''
  if (item.payType === 'bank') return iban
  const str = String(iban)
  if (str.length <= 5) return str
  return '****' + str.slice(-5)
}

const formatPercentage = (value) => {
  if (value === undefined || value === null) return 'N/A'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return 'N/A'
  return `${(num * 100).toFixed(2)}%`
}

const completeNow = (item) => {
  dataFrom.value = item
  completeConfirm.value = true
}

const countDownFinish = (item) => {
  item.hiddenEp = true
}

const handleCancel = (item) => {
  showConfirmDialog({
    title: 'Give Up Order',
    message: 'Are you sure you want to release this order?',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
  }).then(async () => {
    await updatePay_orderCancel({id: item.id})
    showToast('Order released')
    queryParams.value.pageNum = 0
    ordersList.value = []
    onLoadList()
  }).catch(() => {
    // 用户点击取消，不需要处理
  })
}

const handleInvalid = () => {
  showConfirmDialog({
    title: 'Account Invalid',
    message: 'Report this recipient account as invalid?',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
  }).then(async () => {
    await updatePay_orderInvalid({id: dataFrom.value.id})
    showToast('Reported')
    completeConfirm.value = false
    queryParams.value.pageNum = 0
    ordersList.value = []
    onLoadList()
  }).catch(() => {
    // 用户点击取消，不需要处理
  })
}

const handleSubmit = async () => {
  if (!fileList.value.length) return showToast('Upload proof')
  const formData = new FormData()
  formData.append('id', dataFrom.value.id)
  fileList.value.forEach(f => formData.append('files', f.file))
  try {
    await updatePay_orderConfirmV2(formData)
    showToast('Submitted')
    completeConfirm.value = false
    fileList.value = []
    queryParams.value.pageNum = 0
    ordersList.value = []
    onLoadList()
  } catch (error) {
    showToast(error || 'Submission failed')
  }
}

onMounted(async () => {
  const [userRes] = await Promise.all([
    getUserInfo(),
    ensureReceiverAccountRemarkHideKeywords(),
  ])
  user.value = userRes.data
  // van-list 组件会自动触发 @load 事件，不需要手动调用 onLoadList()
})
</script>

<style scoped>
.order-view {
  background: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 90px;
}

.header {
  background: #fff;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.page-title {
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  color: #1a1a1a;
}

.page-subtitle {
  font-size: 13px;
  color: #8e8e93;
  margin: 4px 0 0;
}

.custom-tabs {
  background: #fff;
  padding: 0 10px;
}

:deep(.van-tabs__nav) {
  background: transparent;
}

:deep(.van-tab) {
  font-size: 15px;
  font-weight: 600;
  color: #8e8e93;
}

:deep(.van-tab--active) {
  color: #ff9f00;
}

.content-area {
  padding: 12px;
}

.search-box {
  margin-bottom: 12px;
}

.order-card {
  background: #fff;
  border-radius: 20px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.amount-section {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.header-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.status-badge .van-icon {
  font-size: 12px;
}

.status-ongoing {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  color: #000;
}

.status-success {
  background: linear-gradient(135deg, #4cd964 0%, #5de275 100%);
  color: #fff;
}

.status-failed {
  background: linear-gradient(135deg, #ff3b30 0%, #ff5e55 100%);
  color: #fff;
}

.status-default {
  background: linear-gradient(135deg, #8e8e93 0%, #a8a8ad 100%);
  color: #fff;
}

.copy-all-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  background: rgba(255, 159, 0, 0.1);
  border: 1px solid rgba(255, 159, 0, 0.3);
  border-radius: 6px;
  font-size: 9px;
  font-weight: 600;
  color: #ff9f00;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.copy-all-btn:hover {
  background: rgba(255, 159, 0, 0.15);
  border-color: rgba(255, 159, 0, 0.5);
  transform: translateY(-1px);
}

.copy-all-btn:active {
  transform: translateY(0);
}

.copy-all-btn .van-icon {
  font-size: 14px;
}

.currency {
  font-size: 11px;
  font-weight: 700;
  color: #ff9f00;
}

.amount {
  font-size: 19px;
  font-weight: 800;
  color: #1a1a1a;
}

.card-body {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  overflow: visible;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  font-size: 10px;
  color: #8e8e93;
  flex-shrink: 0;
  min-width: 70px;
}

.value {
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
}

.value-with-copy {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ff9f00;
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
}

.value-with-copy span {
  color: #1a1a1a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
}

.copyable-field {
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 159, 0, 0.05);
  border: 1.5px solid rgba(255, 159, 0, 0.2);
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.copyable-field:hover {
  background: rgba(255, 159, 0, 0.1);
  border-color: rgba(255, 159, 0, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.15);
}

.copyable-field:active {
  transform: translateY(0);
  background: rgba(255, 159, 0, 0.15);
}

.copy-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.copyable-field:hover .copy-badge {
  opacity: 1;
}

.copy-badge .van-icon {
  font-size: 12px;
  color: #ff9f00;
}

.copy-hint {
  font-size: 9px;
  color: #ff9f00;
  font-weight: 600;
  white-space: nowrap;
  display: none;
}

.copyable-field:hover .copy-hint {
  display: inline;
}

.copy-icon {
  font-size: 18px;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s;
  flex-shrink: 0;
  color: #ff9f00;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  background: rgba(255, 159, 0, 0.1);
  position: relative;
  z-index: 1;
}

.copy-icon:hover {
  background: rgba(255, 159, 0, 0.2);
  color: #ff9f00;
  transform: scale(1.1);
}

.copy-icon:active {
  transform: scale(0.95);
  background: rgba(255, 159, 0, 0.3);
}

.text-ellipsis {
  white-space: nowrap;
}

.card-footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.timer-section {
  display: flex;
  align-items: center;
  gap: 3px;
  color: #ff3b30;
  font-size: 10px;
  font-weight: 700;
}

.footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.footer-row .btn-confirm {
  flex-shrink: 0;
}

.order-no {
  font-size: 11px;
  color: #8e8e93;
  padding: 4px 8px;
  min-width: 0;
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  box-sizing: border-box;
}

.order-no-value {
  color: #8e8e93;
  font-weight: 600;
  word-break: break-all;
  overflow-wrap: break-word;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-time {
  font-size: 10px;
  color: #8e8e93;
  font-weight: 500;
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
}

.actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.btn-cancel {
  border-color: #f2f2f7;
  color: #8e8e93;
  padding: 0 10px;
  font-size: 12px;
  height: 28px;
}

.btn-confirm {
  background: #ff9f00;
  border: none;
  padding: 0 14px;
  font-size: 12px;
  height: 28px;
}

.upload-popup {
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.popup-header {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f2f2f7;
  flex-shrink: 0;
}

.popup-header h3 {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #1a1a1a;
}

.popup-header .van-icon {
  font-size: 20px;
  color: #8e8e93;
  cursor: pointer;
}

.popup-body {
  padding: 20px 24px 24px;
  flex: 1;
  overflow-y: auto;
}

/* Recipient Information Section */
.recipient-info-section {
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-title .van-icon {
  font-size: 16px;
  color: #ff9f00;
}

.info-card {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 12px 16px;
  border: 1px solid #f0f0f5;
}

.info-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f5;
  gap: 12px;
}

.info-item-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-item-row:first-child {
  padding-top: 0;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  min-width: 90px;
  white-space: nowrap;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: right;
  flex: 1;
  word-break: break-word;
  padding-left: 12px;
  min-width: 0;
}

.info-value.highlight {
  color: #ff9f00;
  font-weight: 700;
  font-size: 15px;
}

.account-row {
  flex-direction: row;
  align-items: center;
}

.account-row .info-label {
  margin-bottom: 0;
}

.account-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s;
  text-align: right;
  padding-left: 12px;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.account-value:hover {
  color: #ff9f00;
}

.copy-icon-small {
  font-size: 14px;
  color: #8e8e93;
  flex-shrink: 0;
}

.account-value:hover .copy-icon-small {
  color: #ff9f00;
}

.upload-tip {
  font-size: 13px;
  color: #8e8e93;
  margin-bottom: 16px;
  line-height: 1.5;
}

.custom-uploader {
  width: 100%;
  margin-bottom: 30px;
}

.uploader-placeholder {
  width: 100px;
  height: 100px;
  background: #f8f9fb;
  border: 2px dashed #ff9f00;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ff9f00;
}

.uploader-placeholder span {
  font-size: 11px;
  margin-top: 4px;
}

.popup-actions {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 8px;
}

.btn-submit {
  flex: 1;
  height: 50px;
  font-weight: 700;
  background: #ff9f00;
  border: none;
  font-size: 16px;
}

.btn-invalid {
  flex: 1;
  border-color: #ff3b30;
  color: #ff3b30;
  height: 50px;
  font-size: 15px;
  font-weight: 600;
}

/* H5 Mobile Responsive */
@media (max-width: 480px) {
  .popup-header {
    padding: 14px 16px;
  }

  .popup-header h3 {
    font-size: 16px;
  }

  .popup-body {
    padding: 14px 16px 20px;
  }

  .recipient-info-section {
    margin-bottom: 16px;
  }

  .info-card {
    padding: 10px 12px;
    border-radius: 10px;
  }

  .info-item-row {
    padding: 8px 0;
    flex-wrap: wrap;
  }

  .info-label {
    font-size: 11px;
    min-width: 80px;
  }

  .info-value {
    font-size: 13px;
    padding-left: 8px;
    text-align: right;
  }

  .account-row {
    flex-direction: row;
    align-items: center;
  }

  .account-value {
    text-align: right;
    padding-left: 8px;
  }

  .info-value.highlight {
    font-size: 14px;
  }

  .upload-tip {
    font-size: 12px;
    margin-bottom: 14px;
  }

  .btn-submit,
  .btn-invalid {
    height: 48px;
    font-size: 15px;
  }
}

@media (max-width: 375px) {
  .popup-header {
    padding: 12px 14px;
  }

  .popup-body {
    padding: 12px 14px 18px;
  }

  .info-card {
    padding: 8px 10px;
  }

  .info-item-row {
    padding: 6px 0;
  }
}
</style>
